import React, { useEffect, useRef } from 'react';
import * as R from 'ramda';

// Utilities
import { Box, useMediaQuery } from '@mui/material';
import {
  cardsListStyles,
  cardsPanelContainerStyles,
  isAuthSequencingUpdatesEnabled,
  BASE_AND_CUSTOM_TABS as tabs,
} from 'feature/loading-management/utils/const';
import { useSetLoadingTitle } from 'hooks';

// Component
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import CardSkeletonList from 'feature/loading-management/card-skeleton/CardSkeletonList';
import { Heading, ListTabs } from 'feature/loading-management/shared-components';
import HeaderButtons from './HeaderButtons';
import CardsList from './cards-list/CardsList.connector';
import FiltersContainer from './filters/FiltersContainer';
import NoAuthorizationsAvailable from '../../shared-components/NoAuthorizationsAvailable';

const CardsPanel = ({
  activeAuthSequencingTab,
  authorizationSequencingPending,
  cardsCount,
  currentSelection,
  hasWizardAccess,
  filterIdentifierCount,
  filtersOpen,
  isSequencingWizardEnabled,
  listTabCounts,
  resetCardFilters,
  setActiveAuthSequencingTab,
  setFiltersOpen,
  setWizardOpen,
  tooltip,
  viewBy,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const onLocation = viewBy === 'location';
  // Feature flag: REACT_APP_LD_SEQUENCING_AUTH_LIST
  const isAuthSequencingUpdatesFlagOn = isAuthSequencingUpdatesEnabled();
  const filtersRef = useRef();
  const cardsEmpty = R.isEmpty(cardsCount);

  const title = useSetLoadingTitle(authorizationSequencingPending, currentSelection);

  // close filters on initial mount and change of viewport
  useEffect(() => {
    setFiltersOpen(false);
  }, [lgUp, setFiltersOpen]);

  // handleChange :: Value --> activeAuthSequencingTab
  const handleListTabChange = R.o(setActiveAuthSequencingTab, R.tap(resetCardFilters));

  const renderCards = () => {
    if (authorizationSequencingPending) return <CardSkeletonList numberOfCards={3} />;
    if (!cardsEmpty) return <CardsList />;
    return (
      <Box
        alignItems="center"
        backgroundColor="graphic.lighter"
        display="flex"
        justifyContent="center"
        mt={2}
        sx={{
          '& > div': {
            ml: 0,
            mt: 0,
            px: 4,
            width: 'fit-content',
          },
          '& > div p': {
            whiteSpace: 'nowrap',
          },
        }}
        width="100%"
      >
        <NoAuthorizationsAvailable />
      </Box>
    );
  };

  return (
    <Box {...cardsPanelContainerStyles}>
      <Heading
        headerButtons={
          <HeaderButtons
            authorizationSequencingPending={authorizationSequencingPending}
            filterIdentifierCount={filterIdentifierCount}
            filtersOpen={filtersOpen}
            filtersRef={filtersRef}
            hasWizardAccess={hasWizardAccess}
            isSequencingWizardEnabled={isSequencingWizardEnabled}
            setFiltersOpen={setFiltersOpen}
            setWizardOpen={setWizardOpen}
            tooltip={tooltip}
          />
        }
        listTabs={
          <ListTabs
            activeTab={activeAuthSequencingTab}
            labelCounts={listTabCounts}
            onChange={handleListTabChange}
            pendingState={authorizationSequencingPending}
            singleChip
            tabs={tabs}
          />
        }
        showTabs={!isAuthSequencingUpdatesFlagOn && onLocation}
        title={title}
      />
      <Box {...cardsListStyles} mt={onLocation ? 0 : 3} pb={onLocation ? 7 : 0}>
        {renderCards()}
      </Box>
      <FiltersContainer
        filtersRef={filtersRef}
        handleClose={() => setFiltersOpen(false)}
        lgUp={lgUp}
        open={filtersOpen}
        viewBy={viewBy}
      />
    </Box>
  );
};

CardsPanel.propTypes = {
  activeAuthSequencingTab: string.isRequired,
  authorizationSequencingPending: bool.isRequired,
  cardsCount: arrayOf(shape({})),
  currentSelection: shape({}).isRequired,
  filterIdentifierCount: number.isRequired,
  filtersOpen: bool.isRequired,
  hasWizardAccess: bool.isRequired,
  isSequencingWizardEnabled: bool.isRequired,
  listTabCounts: shape({ BASE: number, CUSTOM: number }).isRequired,
  resetCardFilters: func.isRequired,
  setActiveAuthSequencingTab: func.isRequired,
  setFiltersOpen: func.isRequired,
  setWizardOpen: func.isRequired,
  tooltip: string.isRequired,
  viewBy: string,
};

CardsPanel.defaultProps = {
  cardsCount: null,
  viewBy: '',
};

export default CardsPanel;
